import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Group, Paper, Container, Loader, Text } from "@mantine/core";
import { IconArrowLeft, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { modals } from "@mantine/modals";

import { useBatchJourney } from "../hooks/useBatchJourney";
import { createBatchJourney, deleteBatchJourney } from "../hooks/journey.api";

import { CreateJourneyPayload, JourneyItem } from "./journey.types";
import CurrentState from "./CurrentState";
import JourneyTimeline from "./JourneyTimeline";
import JourneyCalendar from "./JourneyCalendar";
import AddJourneyModal, { STATUSES_REQUIRING_COMMENT } from "./AddJourneyModal";
import { getAllInstructorDetails } from "../../api/dynamodb-db-api";
import { ApiService } from "../../api/api-service";
import { useStudentManagement } from "../hooks/useStudentManagement";
import CreateStudent from "../components/CreateStudent";

import {
  syncJourneyCreationToBatch,
  getBatchDetails,
} from "../utils/batchJourneySync.utils";
import {
  getMissedDates,
  formatToLocalDateISO,
  getJourneyDates,
} from "../utils/date.utils";
import ClassStatusDialog from "./ClassStatusDialog";

import type { ComboboxItem } from "@mantine/core";
import CurrentStateFromBatch from "./CurrentStateFromBatch";

// ── Sweetness Rating (COMMENTED OUT) ──
// import SweetnessRatingModal from "./SweetnessRatingModal";
// import { checkSweetnessRatingRequired } from "../hooks/journey.api";
// import { SweetnessCheckResponse } from "./journey.types";

interface InstructorOption extends ComboboxItem {
  name: string;
}

const BatchJourney = () => {
  const { batchNo } = useParams<{ batchNo: string }>();
  const navigate = useNavigate();

  // SuperAdmin detection
  const isSuperAdmin = useMemo(() => {
    try {
      const roles = JSON.parse(localStorage.getItem("applicationRole") || "[]");
      return roles.some(
        (r: any) => r.appName === "adminpanel" && r.appRole === "superAdmin",
      );
    } catch {
      return false;
    }
  }, []);

  const { journeys, loading, refetch } = useBatchJourney(batchNo!);

  const [openId, setOpenId] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createStudentModalOpen, setCreateStudentModalOpen] = useState(false);
  const [isClassStatusOpen, setIsClassStatusOpen] = useState(false);

  // ── Hold-editing state ──
  const [selectedHoldJourney, setSelectedHoldJourney] = useState<any | null>(null);

  const { studentsDropdown, fetchStudents } = useStudentManagement();

  const [instructorsDropdown, setInstructorsDropdown] = useState<
    InstructorOption[]
  >([]);
  const [initialBatchData, setInitialBatchData] = useState<any | null>(null);


  // ── Sweetness Rating state & guard (COMMENTED OUT) ──
  // const [sweetnessCheck, setSweetnessCheck] = useState<SweetnessCheckResponse>({ required: false });
  // const [sweetnessModalOpen, setSweetnessModalOpen] = useState(false);
  // const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  // const [pendingJourneyPayload, setPendingJourneyPayload] = useState<CreateJourneyPayload | null>(null);
  // const refreshSweetnessCheck = useCallback(async () => {
  //   if (!batchNo) return { required: false } as SweetnessCheckResponse;
  //   const check = await checkSweetnessRatingRequired(batchNo);
  //   setSweetnessCheck(check);
  //   return check;
  // }, [batchNo]);
  //
  // useEffect(() => {
  //   refreshSweetnessCheck();
  // }, [refreshSweetnessCheck, journeys]);

  // guardBeforeMarking — sweetness gate disabled, just proceed immediately
  const guardBeforeMarking = async (proceed: () => void) => {
    proceed();
  };


  const fetchInstructors = useCallback(async () => {
    try {
      const response = await getAllInstructorDetails("instructors");
      const options: InstructorOption[] = response.map((ins: any) => ({
        label: ins.email,
        value: ins.email,
        name: ins.name,
      }));
      setInstructorsDropdown(options);
    } catch (err) {
      console.error("Error fetching instructors", err);
    }
  }, []);

  useEffect(() => {
    fetchStudents();
    fetchInstructors();
  }, [fetchStudents, fetchInstructors]);

  // Fetch initial batch details
  useEffect(() => {
    const loadBatchDetails = async () => {
      if (batchNo) {
        const batchData = await getBatchDetails(batchNo);
        if (batchData) {
          setInitialBatchData(batchData);
        }
      }
    };
    loadBatchDetails();
  }, [batchNo]);

  // Check if date is before the first journey date (batch start)
  const isDateBeforeStart = (date: Date) => {
    if (journeys.length === 0) return false;
    const sortedDates = getJourneyDates(journeys);
    const firstDate = new Date(sortedDates[0]);
    firstDate.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < firstDate;
  };

  //  Function to refresh batch data
  const refreshBatchData = useCallback(async () => {
    if (batchNo) {
      const batchData = await getBatchDetails(batchNo);
      if (batchData) {
        // console.log(
        //   " Refreshing batch data with",
        //   batchData.instructors?.length || 0,
        //   "instructors",
        // );
        setInitialBatchData(batchData);
      }
    }
  }, [batchNo]);
  // Re-fetch batch when the tab regains focus so LMS-updated attended hours show up
  useEffect(() => {
    const onFocus = () => refreshBatchData();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshBatchData]);

  // Handle calendar date selection
  const handleCalendarDateSelect = (date: Date) => {
    if (isDateBeforeStart(date)) {
      notifications.show({
        title: "Invalid Date",
        message: "You cannot mark a journey before the batch start date.",
        color: "red",
      });
      return;
    }
    setSelectedDate(date);

    // Check if it's an existing journey
    const dateStr = formatToLocalDateISO(date);
    const existingJourney = journeys.find((j) => j.journeyDate === dateStr);

    if (existingJourney) {
      // ── Hold date clicked → show End Hold dialog ──
      if (existingJourney.is_hold) {
        setSelectedHoldJourney(existingJourney);
        setIsClassStatusOpen(true);
        return;
      }
      // Other existing journey — no action (already marked)
    } else {
      // New entry - check sweetness gate first, then missed dates
      setSelectedHoldJourney(null);
      guardBeforeMarking(() => {
        const missed = getMissedDates(journeys, date);
        if (missed.length > 0 && journeys.length > 0) {
          notifications.show({
            title: "Missed Dates",
            message: `Please fill ${missed.length} missed date(s) first`,
            color: "yellow",
          });
          setIsAddModalOpen(true);
        } else {
          setIsClassStatusOpen(true);
        }
      });
    }
  };
  // const handleMarkJourneyClick = async () => {
  //   //  Refresh batch data before opening modal
  //   await refreshBatchData();

  //   setSelectedDate(new Date());

  //   if (journeys.length === 0) {
  //     const missed = getMissedDates([], new Date());
  //     if (missed.length > 0) {
  //       notifications.show({
  //         title: "Mark Missing Dates First",
  //         message: `Please mark all dates from batch start date first`,
  //         color: "yellow",
  //       });
  //       setIsAddModalOpen(true);
  //     } else {
  //       setIsClassStatusOpen(true);
  //     }
  //   } else {
  //     setIsClassStatusOpen(true);
  //   }
  // };
  // Handle "Mark Journey" button click
  // const handleMarkJourneyClick = () => {
  //   setSelectedDate(new Date());

  //   // REQUIREMENT #8: First Journey flow for already created batch
  //   if (journeys.length ===h 0) {
  //     // First journey ever - check for missed dates from batch start
  //     const missed = getMissedDates([], new Date());
  //     if (missed.length > 0) {
  //       notifications.show({
  //         title: "Mark Missing Dates First",
  //         message: `Please mark all dates from batch start date first`,
  //         color: "yellow",
  //       });
  //       setIsAddModalOpen(true);
  //     } else {
  //       setIsClassStatusOpen(true);
  //     }
  //   } else {
  //     setIsClassStatusOpen(true);
  //   }
  // };

  // Handle "Yes" from class status dialog
  // const handleMarkJourneyClick = async () => {
  //   // Refresh batch data before opening modal
  //   await refreshBatchData();

  //   setSelectedDate(new Date());

  //   // Check if there are any MARKED journeys
  //   const markedJourneys = journeys.filter((j) => j.is_marked);

  //   if (markedJourneys.length === 0) {
  //     // No marked journeys - this is effectively the first entry
  //     const missed = getMissedDates([], new Date());
  //     if (missed.length > 0) {
  //       notifications.show({
  //         title: "Mark Missing Dates First",
  //         message: `Please mark all dates from batch start date first`,
  //         color: "yellow",
  //       });
  //       setIsAddModalOpen(true);
  //     } else {
  //       setIsClassStatusOpen(true);
  //     }
  //   } else {
  //     setIsClassStatusOpen(true);
  //   }
  // };
  const handleMarkJourneyClick = async () => {
  guardBeforeMarking(async () => {
    await refreshBatchData();
    setSelectedDate(new Date());

    const markedJourneys = journeys.filter((j) => j.is_marked);
    if (markedJourneys.length === 0) {
      const missed = getMissedDates([], new Date());
      if (missed.length > 0) {
        notifications.show({
          title: "Mark Missing Dates First",
          message: `Please mark all dates from batch start date first`,
          color: "yellow",
        });
        setIsAddModalOpen(true);
      } else {
        setIsClassStatusOpen(true);
      }
    } else {
      setIsClassStatusOpen(true);
    }
  });
};
  const handleClassHappened = () => {
    setIsClassStatusOpen(false);
    setIsAddModalOpen(true);
  };


  const handleQuickSave = async (
    status: "Leave" | "Holiday" | "Not Started"|  "Not Schedule",
    issueData?: { is_issue: boolean; issue_description: string },
  ) => {
    if (!selectedDate) return;

    modals.openConfirmModal({
      title: `Confirm ${status}`,
      children: (
        <Text size="sm">
          Are you sure you want to mark <strong>{status}</strong> for{" "}
          {selectedDate.toLocaleDateString()}?
        </Text>
      ),
      labels: { confirm: "Yes, Confirm", cancel: "Cancel" },
      confirmProps: {
        color:
          status === "Leave"
            ? "orange"
            : status === "Not Started"
              ? "grape"
              : status === "Not Schedule"
                ? "indigo"
                : "blue",
      },
      onConfirm: async () => {
        const missed = getMissedDates(journeys, selectedDate);
        if (missed.length > 0) {
          setIsClassStatusOpen(false);
          setIsAddModalOpen(true);
          notifications.show({
            title: "Missed Dates",
            message: `Please fill ${missed.length} missed date(s) before marking ${formatToLocalDateISO(selectedDate)}`,
            color: "yellow",
          });
          return;
        }

        try {
          setIsSubmitting(true);

          const lastJourney = journeys.length > 0 ? journeys[0] : null;

          //  Get current trainer from BATCH (not journey)
          const currentTrainerEmail = initialBatchData?.trainerEmail || "";
          const currentTrainerName = initialBatchData?.trainerName || "";

          // console.log(" Current trainer from batch:", currentTrainerEmail);

          // Student attendance based on status
          const studentAttendance =
            status === "Leave"
              ? "Absent"
              : status === "Not Started"
                ? "Not Started"
                : status === "Not Schedule"
                  ? "Not Schedule"
                  : "Holiday";

          //  CRITICAL FIX: Only update CURRENT trainer's attendance
          let updatedInstructors;

          if (
            lastJourney &&
            lastJourney.instructors &&
            lastJourney.instructors.length > 0
          ) {
            // Has previous journey - preserve all instructors but only update current
            updatedInstructors = lastJourney.instructors.map((i: any) => {
              const isCurrentTrainer =
                i.email.toLowerCase() === currentTrainerEmail.toLowerCase();

              // console.log(` Instructor: ${i.name} (${i.email})`);
              // console.log(
              //   `   Is current: ${isCurrentTrainer}, Current attendance: ${i.attendance}`,
              // );

              if (isCurrentTrainer) {
                // console.log(`    Updating to: ${status}`);
                return {
                  ...i,
                  attendance: status,
                };
              } else {
                // console.log(`   ⏭  Keeping: ${i.attendance}`);
                return { ...i };
              }
            });
          } else {
            // No previous journey - load ALL instructors from batch
            const batchInstructorList =
              initialBatchData?.instructors ||
              initialBatchData?.instructor ||
              [];

            // console.log(
            //   ` Loading ${batchInstructorList.length} instructors from batch`,
            // );

            updatedInstructors = batchInstructorList.map((i: any) => {
              const isCurrentTrainer =
                i.email.toLowerCase() === currentTrainerEmail.toLowerCase();

              // console.log(` Batch Instructor: ${i.name} (${i.email})`);
              // console.log(`   Is current: ${isCurrentTrainer}`);

              if (isCurrentTrainer) {
                // console.log(`    Setting to: ${status}`);
                return {
                  name: i.name,
                  email: i.email,
                  // Attended Hours UI hidden, but still snapshot for data consistency
                  instructorClasses: i.attendedHours || "0",
                  attendance: status,
                };
              } else {
                return {
                  name: i.name,
                  email: i.email,
                  // Attended Hours UI hidden, but still snapshot for data consistency
                  instructorClasses: i.attendedHours || "0",
                  attendance: "Changed",
                };
              }
            });
          }

          // console.log(" Final instructors:", updatedInstructors);

          const quickPayload = {
            changeSummary:
              status === "Leave"
                ? `${currentTrainerName} on leave`
                : status === "Not Started"
                  ? `Not Started`
                  : status === "Not Schedule"
                    ? `Not Schedule`
                    : `Holiday - no class`,
            createdBy: "admin",
            numSessions: lastJourney
              ? lastJourney.numSessions
              : initialBatchData?.numSessions || 0,
            trainerName: currentTrainerName,
            trainerEmail: currentTrainerEmail,
            //  Keep existing batch status, NOT Leave/Holiday/Not Started
            batchStatus:
              lastJourney?.batchStatus ||
              initialBatchData?.batchStatus ||
              "On-going",
            students: lastJourney
              ? lastJourney.students.map((s: any) => ({
                  ...s,
                  attendance: studentAttendance,
                }))
              : (initialBatchData?.students || []).map((s: any) => ({
                  name: s.name,
                  email: s.email,
                  studentClasses: s.studentClasses || "",
                  attendance: studentAttendance,
                  studentStatus: s.studentStatus || "Active",
                })),
            instructors: updatedInstructors,
            is_marked: true,
            journeyDate: formatToLocalDateISO(selectedDate),
            // ── Issue tracking from Leave confirmation ──
            ...(issueData?.is_issue && {
              is_issue: true,
              issue_description: issueData.issue_description || "",
            }),
          };

          // console.log(" Saving journey with payload:", {
          //   instructorsCount: quickPayload.instructors.length,
          //   batchStatus: quickPayload.batchStatus,
          // });

          await createBatchJourney(batchNo!, quickPayload);

          // Optimistic UI — close dialog immediately
          setIsClassStatusOpen(false);
          setSelectedDate(new Date());
          setIsSubmitting(false);

          notifications.show({
            title: "Success",
            message:
              status === "Not Started"
                ? `Journey marked as Not Started for ${quickPayload.journeyDate}`
                : status === "Not Schedule"
                  ? `Journey marked as Not Schedule for ${quickPayload.journeyDate}`
                : `Journey marked - ${currentTrainerName} is on ${status}`,
            color:
              status === "Leave"
                ? "orange"
                : status === "Not Started"
                  ? "grape"
                  : status === "Not Schedule"
                    ? "indigo"
                    : "blue",
          });

          // Background sync + refresh in parallel (non-blocking)
          Promise.all([
            syncJourneyCreationToBatch(batchNo!, {
              trainerName: quickPayload.trainerName,
              trainerEmail: quickPayload.trainerEmail,
              students: quickPayload.students,
              instructors: quickPayload.instructors,
              numSessions: quickPayload.numSessions,
            }),
            refetch(),
          ]).catch((err) => {
            console.error("Background sync/refresh failed:", err);
          });
        } catch (error: any) {
          setIsSubmitting(false);

          // ── Handle Sweetness Rating gate (409) — COMMENTED OUT ──
          // const errData = error?.response?.data;
          // if (error?.response?.status === 409 && errData?.error === "SWEETNESS_RATING_REQUIRED") {
          //   setSweetnessCheck({
          //     required: true,
          //     journey_id: errData.journey_id,
          //     journeyDate: errData.journeyDate,
          //     milestoneCount: errData.milestoneCount,
          //     students: errData.students,
          //   });
          //
          //   setIsClassStatusOpen(false);
          //   setSweetnessModalOpen(true);
          //
          //   notifications.show({
          //     title: "Student Rating Required",
          //     message: `Please rate students for journey #${errData.milestoneCount} first`,
          //     color: "pink",
          //   });
          //   return;
          // }

          console.error("Failed to quick-save journey:", error);
          notifications.show({
            title: "Error",
            message: "Failed to save journey entry",
            color: "red",
          });
        }
      },
    });
  };

  // Handle "Guest Lecture" — class happens with a substitute trainer
  const handleGuestLecture = async (
    guestEmail: string,
    guestName: string,
    numSessions: number,
    guestStudentsList: { name: string; email: string; studentClasses: string }[],
  ) => {
    if (!selectedDate) return;

    modals.openConfirmModal({
      title: "Confirm Guest Lecture",
      children: (
        <Text size="sm">
          Assign <strong>{guestName}</strong> as guest lecturer for{" "}
          {selectedDate.toLocaleDateString()}?<br />
          The current trainer will be marked as <strong>Leave</strong>.
        </Text>
      ),
      labels: { confirm: "Yes, Confirm", cancel: "Cancel" },
      confirmProps: { color: "teal" },
      onConfirm: async () => {
        const missed = getMissedDates(journeys, selectedDate);
        if (missed.length > 0) {
          setIsClassStatusOpen(false);
          setIsAddModalOpen(true);
          notifications.show({
            title: "Missed Dates",
            message: `Please fill ${missed.length} missed date(s) before marking ${formatToLocalDateISO(selectedDate)}`,
            color: "yellow",
          });
          return;
        }

        try {
          setIsSubmitting(true);

          const lastJourney = journeys.length > 0 ? journeys[0] : null;

          // Get current trainer from batch
          const currentTrainerEmail = initialBatchData?.trainerEmail || "";
          const currentTrainerName = initialBatchData?.trainerName || "";

          // Build instructors array:
          // - Current trainer → Leave
          // - Guest trainer → Present
          // - Other instructors → preserve
          let updatedInstructors;

          if (
            lastJourney &&
            lastJourney.instructors &&
            lastJourney.instructors.length > 0
          ) {
            // Update current trainer to Leave, preserve others
            updatedInstructors = lastJourney.instructors.map((i: any) => {
              const isCurrentTrainer =
                i.email.toLowerCase() === currentTrainerEmail.toLowerCase();
              if (isCurrentTrainer) {
                return { ...i, attendance: "Leave" };
              }
              return { ...i };
            });
          } else {
            // No previous journey — load from batch
            const batchInstructorList =
              initialBatchData?.instructors ||
              initialBatchData?.instructor ||
              [];

            updatedInstructors = batchInstructorList.map((i: any) => {
              const isCurrentTrainer =
                i.email.toLowerCase() === currentTrainerEmail.toLowerCase();
              if (isCurrentTrainer) {
                return {
                  name: i.name,
                  email: i.email,
                  // Attended Hours UI hidden, but still snapshot for data consistency
                  instructorClasses: i.attendedHours || "0",
                  attendance: "Leave",
                };
              }
              return {
                name: i.name,
                email: i.email,
                // Attended Hours UI hidden, but still snapshot for data consistency
                instructorClasses: i.attendedHours || "0",
                attendance: "Changed",
              };
            });
          }

          // Guest trainer is NOT added to instructors[] array.
          // They are tracked via dedicated fields: guest_trainer_name, guest_trainer_email

          // Students → Use user-provided values with attendance Present,
          // carrying forward each student's last-saved status by email
          const lastStatusByEmail = new Map<string, string>();
          (lastJourney?.students || []).forEach((s: any) => {
            if (s.email && s.studentStatus) {
              lastStatusByEmail.set(s.email.toLowerCase(), s.studentStatus);
            }
          });
          const updatedStudents = guestStudentsList.map((s) => ({
            name: s.name,
            email: s.email,
            studentClasses: s.studentClasses,
            attendance: "Present" as const,
            studentStatus: (lastStatusByEmail.get(
              (s.email || "").toLowerCase(),
            ) || "Active") as JourneyItem["students"][number]["studentStatus"],
          }));

          const quickPayload = {
            changeSummary: `Guest Lecture by ${guestName} (${currentTrainerName} on leave)`,
            createdBy: "admin",
            numSessions: numSessions,
            trainerName: currentTrainerName,
            trainerEmail: currentTrainerEmail,
            batchStatus:
              lastJourney?.batchStatus ||
              initialBatchData?.batchStatus ||
              "On-going",
            students: updatedStudents,
            instructors: updatedInstructors,
            is_marked: true,
            journeyDate: formatToLocalDateISO(selectedDate),
            // Guest lecture tracking fields
            is_guest_lecture: true,
            guest_trainer_name: guestName,
            guest_trainer_email: guestEmail,
          };

          await createBatchJourney(batchNo!, quickPayload);

          // Optimistic UI — close dialog immediately
          setIsClassStatusOpen(false);
          setSelectedDate(new Date());
          setIsSubmitting(false);

          notifications.show({
            title: "Success",
            message: `Guest Lecture assigned to ${guestName} — ${currentTrainerName} marked as Leave`,
            color: "teal",
          });

          // Background sync + refresh in parallel (non-blocking)
          Promise.all([
            syncJourneyCreationToBatch(batchNo!, {
              trainerName: quickPayload.trainerName,
              trainerEmail: quickPayload.trainerEmail,
              students: quickPayload.students,
              instructors: quickPayload.instructors,
              numSessions: quickPayload.numSessions,
            }),
            refetch(),
          ]).catch((err) => {
            console.error("Background sync/refresh failed:", err);
          });
        } catch (error) {
          console.error("Failed to save guest lecture journey:", error);
          notifications.show({
            title: "Error",
            message: "Failed to save guest lecture entry",
            color: "red",
          });
          setIsSubmitting(false);
        }
      },
    });
  };

  // Handle "Hold" — pause batch for a date range
  const handleHoldBatch = async (fromDate: string, toDate: string, comment: string) => {
    if (!batchNo) return;

    modals.openConfirmModal({
      title: "Confirm Hold",
      children: (
        <Text size="sm">
          Put batch <strong>{batchNo}</strong> on hold from{" "}
          <strong>{fromDate}</strong> to <strong>{toDate}</strong>?
          <br />
          Comment: {comment}
        </Text>
      ),
      labels: { confirm: "Yes, Hold", cancel: "Cancel" },
      confirmProps: { color: "yellow" },
      onConfirm: async () => {
        try {
          setIsSubmitting(true);
          setIsClassStatusOpen(false);

          const lastJourney = journeys.length > 0 ? journeys[0] : null;
          const currentTrainerEmail = initialBatchData?.trainerEmail || "";
          const currentTrainerName = initialBatchData?.trainerName || "";

          // Build date range (include ALL days — Sundays too)
          const dates: string[] = [];
          const start = new Date(fromDate + "T00:00:00");
          const end = new Date(toDate + "T00:00:00");
          for (
            let d = new Date(start);
            d <= end;
            d.setDate(d.getDate() + 1)
          ) {
            dates.push(formatToLocalDateISO(d));
          }

          if (dates.length === 0) {
            notifications.show({
              title: "No Dates",
              message: "No valid dates in the selected range.",
              color: "yellow",
            });
            setIsSubmitting(false);
            return;
          }

          // Build instructors with trainer attendance = "Holiday"
          let holdInstructors;
          if (
            lastJourney &&
            lastJourney.instructors &&
            lastJourney.instructors.length > 0
          ) {
            holdInstructors = lastJourney.instructors.map((i: any) => ({
              ...i,
              attendance: "Holiday",
            }));
          } else {
            const batchInstructorList =
              initialBatchData?.instructors ||
              initialBatchData?.instructor ||
              [];
            holdInstructors = batchInstructorList.map((i: any) => ({
              name: i.name,
              email: i.email,
              // Attended Hours UI hidden, but still snapshot for data consistency
              instructorClasses: i.attendedHours || "0",
              attendance: "Holiday",
            }));
          }

          // Students -> Absent
          const holdStudents = lastJourney
            ? lastJourney.students.map((s: any) => ({
                ...s,
                attendance: "Absent",
              }))
            : (initialBatchData?.students || []).map((s: any) => ({
                name: s.name,
                email: s.email,
                studentClasses: s.studentClasses || "",
                attendance: "Absent",
                studentStatus: s.studentStatus || "Active",
              }));

          // Create journey entries for each date
          for (const date of dates) {
            const holdPayload = {
              changeSummary: `Batch on Hold: ${comment}`,
              createdBy: "admin",
              numSessions: lastJourney
                ? lastJourney.numSessions
                : initialBatchData?.numSessions || 0,
              trainerName: currentTrainerName,
              trainerEmail: currentTrainerEmail,
              batchStatus: "Hold",
              students: holdStudents,
              instructors: holdInstructors,
              is_marked: true,
              journeyDate: date,
              is_hold: true,
              hold_from_date: fromDate,
              hold_to_date: toDate,
            };
            await createBatchJourney(batchNo, holdPayload);
          }

          // Update batch status to "Hold"
          try {
            await ApiService.getInstance().put("/batches/update-status", {
              batch_no: batchNo,
              batchStatus: "Hold",
              statusComment: comment,
            });
          } catch (err) {
            console.error("Failed to update batch status to Hold:", err);
          }

          setSelectedDate(new Date());
          setIsSubmitting(false);

          notifications.show({
            title: "Batch On Hold",
            message: `Batch held from ${fromDate} to ${toDate} (${dates.length} day(s))`,
            color: "yellow",
          });

          // Refresh
          refetch();

          // Re-fetch batch data to get updated status
          const freshBatch = await getBatchDetails(batchNo);
          if (freshBatch) setInitialBatchData(freshBatch);
        } catch (error) {
          console.error("Failed to hold batch:", error);
          notifications.show({
            title: "Error",
            message: "Failed to put batch on hold",
            color: "red",
          });
          setIsSubmitting(false);
        }
      },
    });
  };

  // Handle "Unhold" — resume batch from Hold
  const handleUnholdBatch = async () => {
    if (!batchNo) return;

    try {
      setIsSubmitting(true);

      await ApiService.getInstance().put("/batches/update-status", {
        batch_no: batchNo,
        batchStatus: "On-going",
      });

      setIsSubmitting(false);

      notifications.show({
        title: "Batch Resumed",
        message: `Batch #${batchNo} is now On-going again`,
        color: "green",
      });

      // Re-fetch batch data to get updated status
      const freshBatch = await getBatchDetails(batchNo);
      if (freshBatch) setInitialBatchData(freshBatch);

      refetch();
    } catch (error) {
      console.error("Failed to unhold batch:", error);
      notifications.show({
        title: "Error",
        message: "Failed to resume batch",
        color: "red",
      });
      setIsSubmitting(false);
    }
  };

  // Handle "End Hold from Date" — delete hold entries from a given date onward, resume batch
  const handleEndHoldFromDate = async (fromDate: string) => {
    if (!batchNo) return;

    try {
      setIsSubmitting(true);

      // Find all hold journeys on or after fromDate
      const holdEntriesToDelete = journeys.filter(
        (j) => j.is_hold && j.journeyDate >= fromDate,
      );

      if (holdEntriesToDelete.length === 0) {
        notifications.show({
          title: "No Hold Entries",
          message: "No hold entries found from this date onward.",
          color: "yellow",
        });
        setIsSubmitting(false);
        return;
      }

      // Delete each hold entry
      for (const entry of holdEntriesToDelete) {
        await deleteBatchJourney(batchNo, entry.journey_id);
      }

      // Set batch status back to On-going
      await ApiService.getInstance().put("/batches/update-status", {
        batch_no: batchNo,
        batchStatus: "On-going",
      });

      setIsSubmitting(false);
      setSelectedHoldJourney(null);

      notifications.show({
        title: "Hold Ended",
        message: `Removed ${holdEntriesToDelete.length} hold entry/entries from ${fromDate}. Batch is now On-going.`,
        color: "green",
      });

      // Refresh data
      const freshBatch = await getBatchDetails(batchNo);
      if (freshBatch) setInitialBatchData(freshBatch);
      refetch();
    } catch (error) {
      console.error("Failed to end hold:", error);
      notifications.show({
        title: "Error",
        message: "Failed to end hold",
        color: "red",
      });
      setIsSubmitting(false);
    }
  };

  // Handle date change in modal
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };

  // Handle edit journey
  const handleEditJourney = (journey: JourneyItem) => {
    const journeyDate = new Date(journey.journeyDate);
    setSelectedDate(journeyDate);

    // REQUIREMENT #5: Show confirmation before editing
    modals.openConfirmModal({
      title: "Edit Journey Entry",
      children: (
        <Text size="sm">Are you sure you want to edit this Journey entry?</Text>
      ),
      labels: { confirm: "Yes, Edit", cancel: "Cancel" },
      confirmProps: { color: "orange" },
      onConfirm: () => {
        setIsAddModalOpen(true);
      },
    });
  };

  // Handle delete journey
  const handleDeleteJourney = (journeyId: string) => {
    modals.openConfirmModal({
      title: "Delete Journey Entry",
      children: (
        <Text size="sm">
          Are you sure you want to delete this journey entry? This action cannot
          be undone.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await deleteBatchJourney(batchNo!, journeyId);
          notifications.show({
            title: "Success",
            message: "Journey entry deleted successfully",
            color: "green",
          });
          refetch();
        } catch (error) {
          console.error("Failed to delete journey:", error);
          notifications.show({
            title: "Error",
            message: "Failed to delete journey entry",
            color: "red",
          });
        }
      },
    });
  };

  // const handleAddJourney = async (payload: CreateJourneyPayload) => {
  //   if (!selectedDate) {
  //     notifications.show({
  //       title: "Error",
  //       message: "Please select a date",
  //       color: "red",
  //     });
  //     return;
  //   }

  //   if (isDateBeforeStart(selectedDate)) {
  //     notifications.show({
  //       title: "Invalid Date",
  //       message: "You cannot mark a journey before the batch start date.",
  //       color: " ",
  //     });
  //     return;
  //   }

  //   try {
  //     setIsSubmitting(true);

  //     const finalStatus =
  //       payload.batchStatus === "Not Started"
  //         ? "On-going"
  //         : payload.batchStatus;

  //     const journeyPayload = {
  //       ...payload,
  //       batchStatus: finalStatus,
  //       journeyDate: formatToLocalDateISO(selectedDate),
  //     };

  //     // Step 1: Create/update journey entry (must complete first)
  //     await createBatchJourney(batchNo!, journeyPayload);

  //     // Step 2: Optimistic UI — close modal immediately
  //     setIsAddModalOpen(false);
  //     setSelectedDate(new Date());
  //     setIsSubmitting(false);

  //     notifications.show({
  //       title: "Success",
  //       message: "Journey saved successfully",
  //       color: "green",
  //     });

  //     // Step 3: Background sync + refresh in parallel (non-blocking)
  //     Promise.all([
  //       syncJourneyCreationToBatch(batchNo!, {
  //         trainerName: payload.trainerName,
  //         trainerEmail: payload.trainerEmail,
  //         students: payload.students,
  //         instructors: payload.instructors,
  //         numSessions: payload.numSessions,
  //         batchStatus: finalStatus,
  //       }),
  //       refetch(),
  //     ])
  //       .then(() => refreshBatchData())
  //       .catch((err) => {
  //         console.error("Background sync/refresh failed:", err);
  //         notifications.show({
  //           title: "Sync Warning",
  //           message: "Journey saved but background sync encountered an issue",
  //           color: "yellow",
  //         });
  //       });
  //   } catch (error) {
  //     console.error(" Failed to save journey:", error);
  //     notifications.show({
  //       title: "Error",
  //       message: "Failed to save journey entry",
  //       color: "red",
  //     });
  //     setIsSubmitting(false);
  //   }
  // };
const handleAddJourney = async (payload: CreateJourneyPayload) => {
  if (!selectedDate) {
    notifications.show({
      title: "Error",
      message: "Please select a date",
      color: "red",
    });
    return;
  }

  if (isDateBeforeStart(selectedDate)) {
    notifications.show({
      title: "Invalid Date",
      message: "You cannot mark a journey before the batch start date.",
      color: "red",
    });
    return;
  }

  try {
    setIsSubmitting(true);

    const finalStatus =
      payload.batchStatus === "Not Started" ? "On-going" : payload.batchStatus;

    const journeyPayload = {
      ...payload,
      batchStatus: finalStatus,
      journeyDate: formatToLocalDateISO(selectedDate),
    };

    // Step 1: Create/update journey entry
    await createBatchJourney(batchNo!, journeyPayload);

    // Step 2: Optimistic UI — close modal immediately
    setIsAddModalOpen(false);
    setSelectedDate(new Date());
    setIsSubmitting(false);

    notifications.show({
      title: "Success",
      message: "Journey saved successfully",
      color: "green",
    });

    // Step 3: Background sync + refresh in parallel (non-blocking)
    Promise.all([
      syncJourneyCreationToBatch(batchNo!, {
        trainerName: payload.trainerName,
        trainerEmail: payload.trainerEmail,
        students: payload.students,
        instructors: payload.instructors,
        numSessions: payload.numSessions,
        batchStatus: finalStatus,
      }),
      refetch(),
    ])
      // Hold / Absconded / Discarded: the Change Summary becomes the tooltip on the Batches table
      .then(() =>
        STATUSES_REQUIRING_COMMENT.includes(finalStatus)
          ? ApiService.getInstance().put("/batches/update-status", {
              batch_no: batchNo,
              batchStatus: finalStatus,
              statusComment: payload.changeSummary,
            })
          : undefined,
      )
      .then(() => refreshBatchData())
      .catch((err) => {
        console.error("Background sync/refresh failed:", err);
        notifications.show({
          title: "Sync Warning",
          message: "Journey saved but background sync encountered an issue",
          color: "yellow",
        });
      });
  } catch (error: any) {
    setIsSubmitting(false);

    // ── Handle Sweetness Rating gate (409) — COMMENTED OUT ──
    // const errData = error?.response?.data;
    // if (error?.response?.status === 409 && errData?.error === "SWEETNESS_RATING_REQUIRED") {
    //   // Save the payload so we can retry the save once rating is submitted
    //   setPendingJourneyPayload(payload);
    //
    //   // Populate the sweetness modal directly from the error response
    //   setSweetnessCheck({
    //     required: true,
    //     journey_id: errData.journey_id,
    //     journeyDate: errData.journeyDate,
    //     milestoneCount: errData.milestoneCount,
    //     students: errData.students,
    //   });
    //
    //   // Close the Add Journey modal, open the Sweetness modal on top
    //   setIsAddModalOpen(false);
    //   setSweetnessModalOpen(true);
    //
    //   notifications.show({
    //     title: "Sweetness Rating Required",
    //     message: `Please rate students for journey #${errData.milestoneCount} first`,
    //     color: "pink",
    //   });
    //   return;
    // }

    console.error("Failed to save journey:", error);
    notifications.show({
      title: "Error",
      message: "Failed to save journey entry",
      color: "red",
    });
  }
};
  const handleCreateStudent = () => {
    setCreateStudentModalOpen(true);
  };

  if (loading) {
    return (
      <Container style={{ textAlign: "center", marginTop: 100 }}>
        <Loader size="lg" />
        <Text mt="md">Loading Journey...</Text>
      </Container>
    );
  }

  // Main Content
  return (
    <Container size="xl" className="p-4 sm:p-6">
      <Paper
        shadow="sm"
        className="p-4 mb-6 bg-gradient-to-r from-[#FC7A69]/5 to-[#221E45]/5 border border-[#FC7A69]/20"
        radius="md"
      >
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="sm">
            <Button
              leftSection={<IconArrowLeft size={16} />}
              variant="subtle"
              onClick={() => navigate("/batches")}
              className="text-[#221E45] hover:bg-[#FC7A69]/10"
              size="sm"
            >
              Back
            </Button>
            <div>
              <h2 className="text-xl font-bold text-[#221E45] mb-0">
                Batch Journey
              </h2>
              <p className="text-xs text-[#221E45]/60 mt-1">#{batchNo}</p>
            </div>
          </Group>

          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleMarkJourneyClick}
            className="bg-[#FC7A69] hover:bg-[#FC7A69]/90 text-white text-sm"
            size="sm"
            radius="md"
          >
            Mark Journey
          </Button>
        </Group>
      </Paper>

      {/*  CONDITIONAL RENDERING: Check if journey is marked */}
      {journeys.length > 0 && journeys[0].is_marked ? (
        //  CASE 1: Has marked journeys - Show Timeline + Calendar
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-[6] min-w-0 space-y-4">
            <CurrentState data={journeys[0]} batchData={initialBatchData} />

            <JourneyTimeline
              journeys={journeys}
              openId={openId}
              onToggle={setOpenId}
              onRefetch={refetch}
              onEditJourney={handleEditJourney}
              onDeleteJourney={handleDeleteJourney}
              isSuperAdmin={isSuperAdmin}
            />
          </div>

          <div className="flex-[4] min-w-0">
            <div className="sticky top-4 max-w-full overflow-hidden">
              <JourneyCalendar
                journeys={journeys}
                onDateSelect={handleCalendarDateSelect}
              />
            </div>
          </div>
        </div>
      ) : (
        //  CASE 2: No marked journeys OR only auto-created initial journey
        // Show Calendar + Current State from Batch
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-[6] min-w-0 space-y-4">
            {/*  Show Current State from Batch Data */}
            {initialBatchData && (
              <CurrentStateFromBatch batchData={initialBatchData} />
            )}

            {/*  Empty State Message */}
            {/* <Paper
              className="p-12 text-center border-2 border-dashed border-[#FC7A69]/30"
              radius="lg"
            >
              <Text size="lg" className="text-[#221E45]/60 mb-4">
                📅 No journey entries found
              </Text>
              <Text size="sm" className="text-[#221E45]/50 mb-6">
                Select a date from the calendar and click "Mark Journey" to
                create your first entry
              </Text>
              <Button
                leftSection={<IconPlus size={16} />}
                onClick={handleMarkJourneyClick}
                className="bg-[#FC7A69] hover:bg-[#FC7A69]/90 text-white"
                size="md"
              >
                Create First Entry
              </Button>
            </Paper> */}
          </div>

          <div className="flex-[4] min-w-0">
            <div className="sticky top-4 max-w-full overflow-hidden">
              <JourneyCalendar
                journeys={journeys}
                onDateSelect={handleCalendarDateSelect}
              />
            </div>
          </div>
        </div>
      )}

      {/* Journey Modal */}
      <AddJourneyModal
        opened={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setSelectedDate(new Date());
        }}
        onSubmit={handleAddJourney}
        isSubmitting={isSubmitting}
        currentBatchData={journeys[0]}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        existingJourneys={journeys}
        studentsDropdown={studentsDropdown}
        instructorsDropdown={instructorsDropdown}
        batchNo={batchNo!}
        onCreateStudent={handleCreateStudent}
        initialBatchData={initialBatchData}
      />

      {/* Create Student Modal */}
      <CreateStudent
        opened={createStudentModalOpen}
        onClose={() => setCreateStudentModalOpen(false)}
        loading={false}
        studentData={{
          student_id: "",
          name: "",
          email: "",
          mobile: "",
          enrollDate: "",
        }}
        onStudentChange={() => {}}
        onSubmit={async () => {
          setCreateStudentModalOpen(false);
          await fetchStudents();
        }}
      />

{/* ── SweetnessRatingModal (COMMENTED OUT) ──
<SweetnessRatingModal
  opened={sweetnessModalOpen}
  batchNo={batchNo!}
  journeyId={sweetnessCheck.journey_id || ""}
  milestoneCount={sweetnessCheck.milestoneCount || 0}
  students={sweetnessCheck.students || []}
  onSubmitted={async () => {
    setSweetnessModalOpen(false);
    await refreshSweetnessCheck();
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }}
/>
<SweetnessRatingModal
  opened={sweetnessModalOpen}
  batchNo={batchNo!}
  journeyId={sweetnessCheck.journey_id || ""}
  milestoneCount={sweetnessCheck.milestoneCount || 0}
  students={sweetnessCheck.students || []}
  onSubmitted={async () => {
    setSweetnessModalOpen(false);
    await refreshSweetnessCheck();

    // Retry the journey save that was blocked
    if (pendingJourneyPayload) {
      const payloadToRetry = pendingJourneyPayload;
      setPendingJourneyPayload(null);
      setIsAddModalOpen(true); // reopen so the state stays consistent
      await handleAddJourney(payloadToRetry);
      return;
    }

    // Fallback: proactive guard flow (opening from Mark Journey / Calendar click)
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  }}
/>
*/}
      {/* Class Status Dialog */}
      <ClassStatusDialog
        opened={isClassStatusOpen}
        onClose={() => {
          setIsClassStatusOpen(false);
          setSelectedHoldJourney(null);
        }}
        selectedDate={selectedDate}
        onClassHappened={handleClassHappened}
        onQuickSave={handleQuickSave}
        onGuestLecture={handleGuestLecture}
        onHoldBatch={handleHoldBatch}
        onUnholdBatch={handleUnholdBatch}
        isSubmitting={isSubmitting}
        instructorsDropdown={instructorsDropdown}
        currentBatchStatus={initialBatchData?.batchStatus}
        // ── Guest lecture data ──
        currentStudents={
          journeys.length > 0 && journeys[0].students
            ? journeys[0].students.map((s: any) => ({
                name: s.name,
                email: s.email,
                studentClasses: s.studentClasses || "0",
              }))
            : (initialBatchData?.students || []).map((s: any) => ({
                name: s.name,
                email: s.email,
                studentClasses: s.studentClasses || "0",
              }))
        }
        currentNumSessions={
          journeys.length > 0
            ? journeys[0].numSessions
            : initialBatchData?.numSessions || 0
        }
        // ── Hold editing props ──
        isHoldDate={!!selectedHoldJourney}
        onEndHoldFromDate={handleEndHoldFromDate}
        holdFromDate={selectedHoldJourney?.hold_from_date}
        holdToDate={selectedHoldJourney?.hold_to_date}
      />
    </Container>
  );
};

export default BatchJourney;
